// // screens/marketplace/MarketplaceScreen.jsx
// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { useNavigate, useSearchParams } from "react-router-dom";
// import {
//   ShoppingCart,
//   Clock,
//   Package,
//   Star,
//   LayoutGrid,
//   List,
//   ShoppingBag,
//   Truck,
//   AlertCircle,
//   ChevronRight,
//   BadgeCheck,
//   Layers,
// } from "lucide-react";
// import { useAuth } from "../../providers/AuthProvider";
// import { useTheme } from "../../hooks/useTheme";
// import { useToast } from "../../providers/ToastProvider";
// import StorefrontHeader from "../../components/StorefrontHeader";
// import { useCart } from "../../contexts/CartContext";

// const money = (v) => `₵${Number(v || 0).toFixed(2)}`;

// // Which products count as "ready to ship"?
// // DROPSHIPPING = ready only. BOTH = both ready and pre-order.
// // AVAILABLE_IN_GHANA = also dropship/ready. Anything else (PRE_ORDER only)
// // is excluded from the ready view.
// const isReadyToShip = (p) =>
//   p.sale_type === "DROPSHIPPING" ||
//   p.sale_type === "BOTH" ||
//   p.sale_type === "AVAILABLE_IN_GHANA";

// const getCategory = (p) =>
//   p.category_name || p.category?.name || p.sale_type_display || "Product";

// const getDiscount = (product, price) => {
//   // Skip discounts for variant products — the "was" price only makes
//   // sense against a variant price the card doesn't know yet.
//   if (product.has_variants) return null;
//   const original =
//     product.base_price_customer || product.compare_at_price || null;
//   const now = Number(price || 0);
//   const was = Number(original || 0);
//   if (!was || was <= now) return null;
//   const pct = Math.round((1 - now / was) * 100);
//   return pct > 0 ? { pct, was } : null;
// };

// // Variant products with no picks can't be added to cart directly.
// // This helper returns the display price for a product card.
// const getDisplayPrice = (product, preorder) => {
//   const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
//   if (product.has_variants) {
//     // For variant products, prefer the variant-level price from the API
//     // if it returns a top-level effective_price. Otherwise show the
//     // parent's fallback which is the minimum of variant prices.
//     const variantPrices = (product.variants || [])
//       .map((v) =>
//         isPreorderProduct && v.effective_pre_order_price
//           ? Number(v.effective_pre_order_price)
//           : Number(v.effective_price || v.base_price_customer)
//       )
//       .filter((n) => !isNaN(n) && n > 0);
//     if (variantPrices.length > 0) return Math.min(...variantPrices);
//   }
//   if (isPreorderProduct && preorder) {
//     return (
//       preorder.item.effective_price ||
//       preorder.item.price_customer ||
//       product.effective_pre_order_price ||
//       product.pre_order_price_customer
//     );
//   }
//   return product.effective_price || product.base_price_customer;
// };

// export default function MarketplaceScreen({ defaultReadyOnly = false }) {
//   const { authFetch, isAuthenticated } = useAuth();
//   const { colors } = useTheme();
//   const { showToast } = useToast();
//   const navigate = useNavigate();
//   const [params, setParams] = useSearchParams();

//   const search = params.get("search") || "";
//   const [products, setProducts] = useState([]);
//   const [campaigns, setCampaigns] = useState([]);
//   const [loading, setLoading] = useState(true);
//   const [viewMode, setViewMode] = useState("grid");
//   const [wishlisted, setWishlisted] = useState(() => new Set());
//   const [readyOnly, setReadyOnly] = useState(defaultReadyOnly);
//   const { increment: incrementCart } = useCart();

//   // ── Fetch once ─────────────────────────────────────────
//   useEffect(() => {
//     let active = true;
//     (async () => {
//       setLoading(true);
//       try {
//         const query = new URLSearchParams();
//         if (search) query.set("search", search);

//         const [pRes, cRes] = await Promise.all([
//           authFetch(`/marketplace/products/?${query}`),
//           authFetch("/marketplace/pre-orders/?is_active=true"),
//         ]);
//         const [pData, cData] = await Promise.all([pRes.json(), cRes.json()]);
//         if (!pRes.ok)
//           throw new Error(pData.message || "Could not load products");
//         if (!active) return;
//         setProducts(pData.data || []);
//         setCampaigns(cRes.ok ? cData.data || [] : []);
//       } catch (err) {
//         showToast(err.message, "error");
//       } finally {
//         if (active) setLoading(false);
//       }
//     })();
//     return () => {
//       active = false;
//     };
//   }, [authFetch, search, showToast]);

//   // ── Derived ────────────────────────────────────────────
//   const campaignByProduct = useMemo(() => {
//     const map = new Map();
//     campaigns.forEach((c) => {
//       const items = c.products || c.pre_order_products || [];
//       items.forEach((item) =>
//         map.set(String(item.product ?? item.product_id), {
//           campaign: c,
//           item,
//         })
//       );
//     });
//     return map;
//   }, [campaigns]);

//   const liveCampaigns = useMemo(
//     () =>
//       campaigns.filter((c) => {
//         const now = Date.now();
//         return (
//           c.is_active &&
//           new Date(c.start_date).getTime() <= now &&
//           new Date(c.end_date).getTime() >= now
//         );
//       }),
//     [campaigns]
//   );

//   const visibleProducts = useMemo(() => {
//     if (!readyOnly) return products;
//     return products.filter(isReadyToShip);
//   }, [products, readyOnly]);

//   const nextClosing = useMemo(() => {
//     if (liveCampaigns.length === 0) return null;
//     return [...liveCampaigns].sort(
//       (a, b) => new Date(a.end_date) - new Date(b.end_date)
//     )[0];
//   }, [liveCampaigns]);

//   // ── Search ─────────────────────────────────────────────
//   const onSearch = (q) => {
//     const next = new URLSearchParams(params);
//     if (q) next.set("search", q);
//     else next.delete("search");
//     setParams(next);
//   };

//   // ── Cart / wishlist ────────────────────────────────────
//   const addToCart = async (product, e) => {
//     e?.stopPropagation();
//     if (!isAuthenticated) return navigate("/login?next=/marketplace");

//     // Variant products need a picked variant — send the user to the
//     // detail page where they can choose options.
//     if (product.has_variants) {
//       navigate(`/marketplace/products/${product.id}`);
//       return;
//     }

//     const preorder = campaignByProduct.get(String(product.id));
//     const itemType =
//       product.sale_type === "PRE_ORDER" && preorder
//         ? "PRE_ORDER"
//         : "DROPSHIPPING";
//     try {
//       const res = await authFetch("/marketplace/cart/add/", {
//         method: "POST",
//         body: JSON.stringify({
//           product: product.id,
//           quantity: 1,
//           item_type: itemType,
//           ...(itemType === "PRE_ORDER" && {
//             pre_order: preorder?.campaign.id,
//           }),
//         }),
//       });
//       const data = await res.json();
//       if (!res.ok) {
//         throw new Error(
//           data.message || Object.values(data.errors || {}).flat().join(" ")
//         );
//       }
//       showToast("Added to cart", "success");
//       incrementCart(1);
//     } catch (err) {
//       showToast(err.message, "error");
//     }
//   };

//   const toggleWishlist = (product, e) => {
//     e?.stopPropagation();
//     if (!isAuthenticated) return navigate("/login?next=/marketplace");
//     setWishlisted((prev) => {
//       const next = new Set(prev);
//       if (next.has(product.id)) next.delete(product.id);
//       else next.add(product.id);
//       return next;
//     });
//   };

//   return (
//     <div style={styles.pageWrap}>
//       <StorefrontHeader
//         title="Shop"
//         subtitle="Everything in the marketplace with your validated store price."
//         onSearch={onSearch}
//         searchValue={search}
//       />

//       {/* Inline filter bar — no refetch, filters the in-memory list */}
//       <div style={styles.filterBar}>
//         <button
//           type="button"
//           onClick={() => setReadyOnly((v) => !v)}
//           style={styles.filterChip(colors, readyOnly)}
//         >
//           <Truck size={14} />
//           Ready to ship only
//           <span style={styles.filterCount(colors, readyOnly)}>
//             {products.filter(isReadyToShip).length}
//           </span>
//         </button>

//         <div style={styles.toolbarRight}>
//           <span style={styles.toolbarCount(colors)}>
//             {visibleProducts.length} product
//             {visibleProducts.length === 1 ? "" : "s"}
//             {search && (
//               <>
//                 {" · "}
//                 <strong style={{ color: colors.text }}>“{search}”</strong>
//               </>
//             )}
//           </span>
//           <div style={styles.viewToggle(colors)}>
//             <ViewToggleBtn
//               active={viewMode === "grid"}
//               onClick={() => setViewMode("grid")}
//               colors={colors}
//               label="Grid"
//             >
//               <LayoutGrid size={15} />
//             </ViewToggleBtn>
//             <ViewToggleBtn
//               active={viewMode === "list"}
//               onClick={() => setViewMode("list")}
//               colors={colors}
//               label="List"
//             >
//               <List size={15} />
//             </ViewToggleBtn>
//           </div>
//         </div>
//       </div>

//       {/* Pre-order nudge — only when a live window exists */}
//       {nextClosing && !readyOnly && (
//         <NudgeStrip
//           campaign={nextClosing}
//           count={liveCampaigns.length}
//           colors={colors}
//           onOpen={() => navigate("/preorder")}
//         />
//       )}

//       {/* Content */}
//       {loading ? (
//         <SkeletonGrid count={8} colors={colors} />
//       ) : visibleProducts.length === 0 ? (
//         <EmptyState
//           colors={colors}
//           search={search}
//           readyOnly={readyOnly}
//           onClear={() => onSearch("")}
//           onShowAll={() => setReadyOnly(false)}
//         />
//       ) : viewMode === "grid" ? (
//         <div className="mp-grid">
//           {visibleProducts.map((product) => (
//             <ProductCard
//               key={product.id}
//               product={product}
//               preorder={campaignByProduct.get(String(product.id))}
//               colors={colors}
//               wishlisted={wishlisted.has(product.id)}
//               onOpen={() => navigate(`/marketplace/products/${product.id}`)}
//               onAdd={(e) => addToCart(product, e)}
//               onWishlist={(e) => toggleWishlist(product, e)}
//             />
//           ))}
//         </div>
//       ) : (
//         <div className="mp-list">
//           {visibleProducts.map((product) => (
//             <ProductRow
//               key={product.id}
//               product={product}
//               preorder={campaignByProduct.get(String(product.id))}
//               colors={colors}
//               onOpen={() => navigate(`/marketplace/products/${product.id}`)}
//               onAdd={(e) => addToCart(product, e)}
//             />
//           ))}
//         </div>
//       )}

//       <style>{`
//         .mp-grid {
//           display: grid;
//           grid-template-columns: repeat(2, minmax(0, 1fr));
//           gap: 18px 12px;
//         }
//         @media (min-width: 560px) {
//           .mp-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px 18px; }
//         }
//         @media (min-width: 900px) {
//           .mp-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 26px 20px; }
//         }
//         @media (min-width: 1200px) {
//           .mp-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 30px 22px; }
//         }
//         .mp-list { display: grid; grid-template-columns: minmax(0, 1fr); gap: 10px; }
//         @media (min-width: 900px) {
//           .mp-list { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
//         }
//         @media (hover: none) { .mp-quick-actions { display: none !important; } }
//         .mp-card:hover .mp-quick-actions,
//         .mp-card:focus-within .mp-quick-actions {
//           opacity: 1 !important; transform: translateX(0) !important;
//         }
//         .mp-card:hover .mp-media img,
//         .mp-card:focus-within .mp-media img { transform: scale(1.04); }
//         @media (hover: hover) and (min-width: 640px) {
//           .mp-add-mobile { display: none !important; }
//         }
//       `}</style>
//     </div>
//   );
// }

// /* ══════════════════════════════════════════════════════════
//    Nudge strip
//    ══════════════════════════════════════════════════════════ */

// const NudgeStrip = ({ campaign, count, colors, onOpen }) => {
//   const end = new Date(campaign.end_date).getTime();
//   const daysLeft = Math.max(0, Math.ceil((end - Date.now()) / 86400000));
//   const urgent = daysLeft <= 2;
//   const fg = urgent ? colors.error : colors.warning;
//   const bg = urgent ? colors.errorBg : colors.warningBg;

//   return (
//     <button type="button" onClick={onOpen} style={styles.nudge(fg, bg)}>
//       <span style={styles.nudgeIcon(fg)}>
//         <Clock size={15} />
//       </span>
//       <div style={{ flex: 1, minWidth: 0 }}>
//         <div style={styles.nudgeTitle(colors)}>
//           {count === 1
//             ? "A pre-order window is open"
//             : `${count} pre-order windows are open`}
//         </div>
//         <div style={styles.nudgeSub(colors)}>
//           {campaign.name} ·{" "}
//           {daysLeft === 0
//             ? "closing today"
//             : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
//         </div>
//       </div>
//       <ChevronRight size={16} color={fg} />
//     </button>
//   );
// };

// /* ══════════════════════════════════════════════════════════
//    Cards
//    ══════════════════════════════════════════════════════════ */

// const ViewToggleBtn = ({ active, onClick, colors, children, label }) => (
//   <button
//     type="button"
//     onClick={onClick}
//     aria-label={label}
//     style={{
//       padding: "7px 9px",
//       border: "none",
//       background: active ? colors.btnBg : "transparent",
//       color: active ? colors.btnText : colors.secondaryText,
//       cursor: "pointer",
//       display: "flex",
//       alignItems: "center",
//       justifyContent: "center",
//     }}
//   >
//     {children}
//   </button>
// );

// const Rating = ({ value, count, colors, compact }) => {
//   const v = Number(value || 0);
//   if (!v) return null;
//   return (
//     <span style={styles.rating(colors)}>
//       <Star
//         size={compact ? 11 : 12}
//         fill={colors.warning}
//         color={colors.warning}
//       />
//       <span style={{ color: colors.text, fontWeight: 700 }}>{v.toFixed(1)}</span>
//       {!compact && count > 0 && <span>({count})</span>}
//     </span>
//   );
// };

// const StatusPill = ({ status, colors, floating }) => {
//   const Icon = status.icon;
//   return (
//     <span
//       style={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 4,
//         padding: "5px 11px",
//         borderRadius: 999,
//         fontSize: 10.5,
//         fontWeight: 700,
//         letterSpacing: 0.2,
//         textTransform: "uppercase",
//         whiteSpace: "nowrap",
//         background: status.bg,
//         color: status.fg,
//         ...(floating
//           ? {
//               position: "absolute",
//               top: 10,
//               left: 10,
//               backdropFilter: "blur(8px)",
//             }
//           : {}),
//       }}
//     >
//       <Icon size={10} />
//       {status.label}
//     </span>
//   );
// };

// const DiscountBadge = ({ pct, colors }) => (
//   <span
//     style={{
//       position: "absolute",
//       top: 10,
//       left: 10,
//       padding: "5px 10px",
//       borderRadius: 999,
//       background: colors.btnBg,
//       color: colors.btnText,
//       fontSize: 10.5,
//       fontWeight: 800,
//       letterSpacing: 0.2,
//     }}
//   >
//     -{pct}%
//   </span>
// );

// const GhanaBadge = ({ colors }) => (
//   <span
//     style={{
//       position: "absolute",
//       top: 10,
//       right: 10,
//       display: "inline-flex",
//       alignItems: "center",
//       gap: 4,
//       padding: "5px 10px",
//       borderRadius: 999,
//       background: colors.raisedBg,
//       color: colors.btnBg,
//       fontSize: 10,
//       fontWeight: 800,
//       letterSpacing: 0.3,
//       textTransform: "uppercase",
//       boxShadow: `0 2px 8px ${colors.shadow}`,
//     }}
//   >
//     <BadgeCheck size={10} />
//     Ghana
//   </span>
// );

// const ShippingBadge = ({ colors, fee }) => {
//   if (!fee || fee <= 0) return null;
//   return (
//     <span
//       style={{
//         display: "inline-flex",
//         alignItems: "center",
//         gap: 3,
//         fontSize: 10.5,
//         color: colors.secondaryText,
//         fontWeight: 600,
//       }}
//     >
//       <Truck size={10} /> +{money(fee)}
//     </span>
//   );
// };

// const VariantHint = ({ colors, count }) => (
//   <span
//     style={{
//       display: "inline-flex",
//       alignItems: "center",
//       gap: 4,
//       fontSize: 10.5,
//       fontWeight: 700,
//       padding: "2px 7px",
//       borderRadius: 999,
//       background: colors.hoverBg,
//       color: colors.secondaryText,
//       letterSpacing: 0.2,
//     }}
//   >
//     <Layers size={10} />
//     {count} variants
//   </span>
// );

// const QuickActions = ({ colors, onAdd, canBuy, onWishlist, wishlisted }) => (
//   <div
//     className="mp-quick-actions"
//     style={{
//       position: "absolute",
//       top: 10,
//       right: 10,
//       display: "flex",
//       flexDirection: "column",
//       gap: 8,
//       opacity: 0,
//       transform: "translateX(6px)",
//       transition: "opacity 0.2s ease, transform 0.2s ease",
//     }}
//   >
//     <QuickBtn
//       colors={colors}
//       aria-label="Wishlist"
//       onClick={(e) => {
//         e.stopPropagation();
//         onWishlist?.();
//       }}
//     >
//       <ShoppingBag
//         size={14}
//         fill={wishlisted ? colors.error : "none"}
//         color={wishlisted ? colors.error : colors.text}
//       />
//     </QuickBtn>
//     <QuickBtn
//       colors={colors}
//       aria-label="Add to cart"
//       onClick={(e) => {
//         e.stopPropagation();
//         if (canBuy) onAdd(e);
//       }}
//       disabled={!canBuy}
//     >
//       <ShoppingCart
//         size={14}
//         color={canBuy ? colors.text : colors.secondaryText}
//       />
//     </QuickBtn>
//   </div>
// );

// const QuickBtn = ({ colors, children, onClick, disabled, ...rest }) => (
//   <button
//     type="button"
//     onClick={onClick}
//     disabled={disabled}
//     {...rest}
//     style={{
//       width: 34,
//       height: 34,
//       borderRadius: "50%",
//       border: "none",
//       background: colors.raisedBg,
//       color: colors.text,
//       cursor: disabled ? "not-allowed" : "pointer",
//       display: "inline-flex",
//       alignItems: "center",
//       justifyContent: "center",
//       boxShadow: `0 2px 8px ${colors.shadow}`,
//       opacity: disabled ? 0.6 : 1,
//     }}
//   >
//     {children}
//   </button>
// );

// const ProductCard = ({
//   product,
//   preorder,
//   colors,
//   onOpen,
//   onAdd,
//   onWishlist,
//   wishlisted,
// }) => {
//   const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
//   const hasVariants = !!product.has_variants;
//   const isAvailableInGhana = product.sale_type === "AVAILABLE_IN_GHANA";
//   const shownPrice = getDisplayPrice(product, preorder);

//   const inStock = product.is_in_stock;
//   const canBuy = !hasVariants && (isPreorderProduct ? !!preorder : inStock);
//   const discount = getDiscount(product, shownPrice);

//   const shippingFee = Number(product.shipping_fee || 0);

//   const status = hasVariants
//     ? {
//         label: "Options",
//         fg: colors.linkColor,
//         bg: colors.hoverBg,
//         icon: Layers,
//       }
//     : isPreorderProduct
//     ? preorder
//       ? {
//           label: "Pre-order",
//           fg: colors.warning,
//           bg: colors.warningBg,
//           icon: Clock,
//         }
//       : {
//           label: "No window",
//           fg: colors.secondaryText,
//           bg: colors.hoverBg,
//           icon: Clock,
//         }
//     : inStock
//     ? {
//         label: "In stock",
//         fg: colors.success,
//         bg: colors.successBg,
//         icon: Truck,
//       }
//     : {
//         label: "Sold out",
//         fg: colors.secondaryText,
//         bg: colors.hoverBg,
//         icon: AlertCircle,
//       };

//   const img =
//     product.thumbnail_image_url ||
//     product.main_image_url ||
//     product.main_image;

//   return (
//     <article
//       onClick={onOpen}
//       className="mp-card"
//       style={{ cursor: "pointer", minWidth: 0 }}
//     >
//       <div className="mp-media" style={styles.media(colors)}>
//         {img ? (
//           <img
//             src={img}
//             alt={product.name}
//             loading="lazy"
//             style={styles.mediaImg}
//           />
//         ) : (
//           <div style={styles.mediaPlaceholder(colors)}>
//             <Package size={28} />
//           </div>
//         )}

//         {discount && !isPreorderProduct && !hasVariants ? (
//           <DiscountBadge pct={discount.pct} colors={colors} />
//         ) : (
//           <StatusPill status={status} colors={colors} floating />
//         )}

//         {isAvailableInGhana && <GhanaBadge colors={colors} />}

//         <QuickActions
//           colors={colors}
//           canBuy={canBuy}
//           onAdd={onAdd}
//           onWishlist={onWishlist}
//           wishlisted={wishlisted}
//         />
//       </div>

//       <div style={styles.metaRow}>
//         <span style={styles.category(colors)}>{getCategory(product)}</span>
//         {hasVariants ? (
//           <VariantHint
//             colors={colors}
//             count={(product.variants || []).length}
//           />
//         ) : (
//           <Rating
//             value={product.average_rating}
//             count={product.review_count}
//             colors={colors}
//             compact
//           />
//         )}
//       </div>

//       <h3 style={styles.cardTitle(colors)}>{product.name}</h3>

//       <div style={styles.priceRow}>
//         {hasVariants && (
//           <span style={styles.fromLabel(colors)}>From</span>
//         )}
//         <span style={styles.price(colors)}>{money(shownPrice)}</span>
//         {discount && (
//           <span style={styles.oldPrice(colors)}>{money(discount.was)}</span>
//         )}
//         {shippingFee > 0 && (
//           <ShippingBadge colors={colors} fee={shippingFee} />
//         )}
//       </div>

//       <button
//         type="button"
//         disabled={!canBuy && !hasVariants}
//         onClick={onAdd}
//         className="mp-add-mobile"
//         style={styles.mobileAddBtn(colors, canBuy || hasVariants)}
//       >
//         {hasVariants
//           ? "Choose options"
//           : isPreorderProduct
//           ? preorder
//             ? "Join pre-order"
//             : "Not in a window"
//           : inStock
//           ? "Add to cart"
//           : "Sold out"}
//       </button>
//     </article>
//   );
// };

// const ProductRow = ({ product, preorder, colors, onOpen, onAdd }) => {
//   const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
//   const hasVariants = !!product.has_variants;
//   const isAvailableInGhana = product.sale_type === "AVAILABLE_IN_GHANA";
//   const shownPrice = getDisplayPrice(product, preorder);

//   const inStock = product.is_in_stock;
//   const canBuy = !hasVariants && (isPreorderProduct ? !!preorder : inStock);
//   const shippingFee = Number(product.shipping_fee || 0);

//   const status = hasVariants
//     ? {
//         label: "Options",
//         fg: colors.linkColor,
//         bg: colors.hoverBg,
//         icon: Layers,
//       }
//     : isPreorderProduct
//     ? preorder
//       ? {
//           label: "Pre-order",
//           fg: colors.warning,
//           bg: colors.warningBg,
//           icon: Clock,
//         }
//       : {
//           label: "No window",
//           fg: colors.secondaryText,
//           bg: colors.hoverBg,
//           icon: Clock,
//         }
//     : inStock
//     ? {
//         label: "In stock",
//         fg: colors.success,
//         bg: colors.successBg,
//         icon: Truck,
//       }
//     : {
//         label: "Sold out",
//         fg: colors.secondaryText,
//         bg: colors.hoverBg,
//         icon: AlertCircle,
//       };

//   const img =
//     product.thumbnail_image_url ||
//     product.main_image_url ||
//     product.main_image;

//   return (
//     <article
//       onClick={onOpen}
//       style={styles.listCard(colors)}
//       onMouseEnter={(e) => {
//         e.currentTarget.style.borderColor = colors.btnBg;
//       }}
//       onMouseLeave={(e) => {
//         e.currentTarget.style.borderColor = colors.border;
//       }}
//     >
//       <div style={styles.listThumb(colors)}>
//         {img ? (
//           <img
//             src={img}
//             alt={product.name}
//             loading="lazy"
//             style={{ width: "100%", height: "100%", objectFit: "cover" }}
//           />
//         ) : (
//           <div style={styles.mediaPlaceholder(colors)}>
//             <Package size={22} />
//           </div>
//         )}
//       </div>
//       <div
//         style={{
//           flex: 1,
//           minWidth: 0,
//           display: "flex",
//           flexDirection: "column",
//         }}
//       >
//         <div style={styles.listTopRow}>
//           <h3 style={styles.listTitle(colors)}>{product.name}</h3>
//           <StatusPill status={status} colors={colors} />
//         </div>
//         <div
//           style={{
//             display: "flex",
//             alignItems: "center",
//             gap: 8,
//             marginTop: 4,
//             flexWrap: "wrap",
//           }}
//         >
//           {isAvailableInGhana && (
//             <span
//               style={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: 3,
//                 fontSize: 10.5,
//                 fontWeight: 700,
//                 padding: "2px 7px",
//                 borderRadius: 999,
//                 background: `${colors.btnBg}18`,
//                 color: colors.btnBg,
//                 letterSpacing: 0.3,
//                 textTransform: "uppercase",
//               }}
//             >
//               <BadgeCheck size={10} /> Ghana
//             </span>
//           )}
//           {hasVariants && (
//             <VariantHint
//               colors={colors}
//               count={(product.variants || []).length}
//             />
//           )}
//         </div>
//         <p style={styles.listDesc(colors)}>
//           {product.short_description || "—"}
//         </p>
//         <div style={styles.listBottomRow}>
//           <div
//             style={{
//               display: "flex",
//               alignItems: "baseline",
//               gap: 8,
//               flexWrap: "wrap",
//             }}
//           >
//             {hasVariants && (
//               <span style={styles.fromLabel(colors)}>From</span>
//             )}
//             <span style={styles.listPrice(colors)}>{money(shownPrice)}</span>
//             {shippingFee > 0 && (
//               <ShippingBadge colors={colors} fee={shippingFee} />
//             )}
//           </div>
//           <button
//             type="button"
//             disabled={!canBuy && !hasVariants}
//             onClick={onAdd}
//             aria-label={
//               hasVariants
//                 ? `Choose options for ${product.name}`
//                 : `Add ${product.name} to cart`
//             }
//             style={styles.basketBtn(colors, canBuy || hasVariants)}
//           >
//             <ShoppingCart size={18} />
//           </button>
//         </div>
//       </div>
//     </article>
//   );
// };

// /* ── Skeleton / Empty ────────────────────────────────────── */

// const SkeletonGrid = ({ count, colors }) => (
//   <div className="mp-grid">
//     {Array.from({ length: count }).map((_, i) => (
//       <div key={i} style={{ minWidth: 0 }}>
//         <div
//           style={{
//             aspectRatio: "1 / 1",
//             borderRadius: 18,
//             background: colors.hoverBg,
//             animation: "mp-skeleton 1.4s ease-in-out infinite",
//           }}
//         />
//         <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
//           <div
//             style={{
//               height: 10,
//               borderRadius: 4,
//               background: colors.hoverBg,
//               width: "60%",
//             }}
//           />
//           <div
//             style={{
//               height: 12,
//               borderRadius: 4,
//               background: colors.hoverBg,
//               width: "80%",
//             }}
//           />
//           <div
//             style={{
//               height: 12,
//               borderRadius: 4,
//               background: colors.hoverBg,
//               width: "40%",
//             }}
//           />
//         </div>
//       </div>
//     ))}
//     <style>{`@keyframes mp-skeleton { 0%,100% { opacity: 0.6; } 50% { opacity: 0.35; } }`}</style>
//   </div>
// );

// const EmptyState = ({ colors, search, readyOnly, onClear, onShowAll }) => (
//   <div style={styles.emptyWrap(colors)}>
//     <div style={styles.emptyIcon(colors)}>
//       {readyOnly ? <Truck size={26} /> : <Package size={26} />}
//     </div>
//     <h3 style={styles.emptyTitle(colors)}>
//       {search
//         ? "No matches"
//         : readyOnly
//         ? "Nothing ready to ship"
//         : "No products yet"}
//     </h3>
//     <p style={styles.emptyText(colors)}>
//       {search
//         ? `Nothing matches “${search}”. Try a different term.`
//         : readyOnly
//         ? "There are no ready-to-ship items right now. Turn off the filter to see everything, or browse pre-order windows."
//         : "The marketplace is being restocked. Check back soon."}
//     </p>
//     <div
//       style={{
//         display: "flex",
//         gap: 10,
//         justifyContent: "center",
//         flexWrap: "wrap",
//       }}
//     >
//       {search && (
//         <button
//           type="button"
//           onClick={onClear}
//           style={styles.emptySecondary(colors)}
//         >
//           Clear search
//         </button>
//       )}
//       {readyOnly && !search && (
//         <button
//           type="button"
//           onClick={onShowAll}
//           style={styles.emptySecondary(colors)}
//         >
//           Show everything
//         </button>
//       )}
//     </div>
//   </div>
// );

// /* ══════════════════════════════════════════════════════════
//    Styles
//    ══════════════════════════════════════════════════════════ */

// const styles = {
//   pageWrap: {
//     maxWidth: 1180,
//     margin: "0 auto",
//     padding: "20px 16px 60px",
//   },

//   filterBar: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 12,
//     marginBottom: 16,
//     flexWrap: "wrap",
//   },
//   filterChip: (c, active) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 8,
//     padding: "8px 12px",
//     borderRadius: 999,
//     border: `1px solid ${active ? c.btnBg : c.border}`,
//     background: active ? c.btnBg : c.raisedBg,
//     color: active ? c.btnText : c.text,
//     fontSize: 13,
//     fontWeight: 600,
//     cursor: "pointer",
//     transition: "all 0.15s ease",
//   }),
//   filterCount: (c, active) => ({
//     fontSize: 11,
//     fontWeight: 800,
//     padding: "1px 7px",
//     borderRadius: 999,
//     background: active ? "rgba(255,255,255,0.22)" : c.hoverBg,
//     color: active ? "#fff" : c.secondaryText,
//   }),
//   toolbarRight: {
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     marginLeft: "auto",
//     flexWrap: "wrap",
//   },
//   toolbarCount: (c) => ({
//     fontSize: 12.5,
//     color: c.secondaryText,
//   }),
//   viewToggle: (c) => ({
//     display: "inline-flex",
//     border: `1px solid ${c.border}`,
//     borderRadius: 10,
//     overflow: "hidden",
//     background: c.raisedBg,
//   }),

//   nudge: (fg, bg) => ({
//     display: "flex",
//     alignItems: "center",
//     gap: 12,
//     width: "100%",
//     padding: "10px 14px",
//     marginBottom: 16,
//     borderRadius: 12,
//     border: `1px solid ${fg}55`,
//     background: bg,
//     cursor: "pointer",
//     textAlign: "left",
//     fontFamily: "inherit",
//   }),
//   nudgeIcon: (fg) => ({
//     width: 32,
//     height: 32,
//     borderRadius: 9,
//     background: fg,
//     color: "#fff",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),
//   nudgeTitle: (c) => ({
//     fontSize: 13,
//     fontWeight: 700,
//     color: c.heading,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//   }),
//   nudgeSub: (c) => ({
//     fontSize: 11.5,
//     color: c.secondaryText,
//     marginTop: 2,
//   }),

//   media: (c) => ({
//     position: "relative",
//     aspectRatio: "1 / 1",
//     borderRadius: 18,
//     overflow: "hidden",
//     background: c.hoverBg,
//   }),
//   mediaImg: {
//     width: "100%",
//     height: "100%",
//     objectFit: "cover",
//     display: "block",
//     transition: "transform 0.5s ease",
//   },
//   mediaPlaceholder: (c) => ({
//     width: "100%",
//     height: "100%",
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "center",
//     color: c.secondaryText,
//   }),
//   metaRow: {
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 8,
//     marginTop: 12,
//   },
//   category: (c) => ({
//     fontSize: 12,
//     color: c.secondaryText,
//     fontWeight: 500,
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     whiteSpace: "nowrap",
//     minWidth: 0,
//   }),
//   rating: (c) => ({
//     display: "inline-flex",
//     alignItems: "center",
//     gap: 3,
//     fontSize: 11.5,
//     color: c.secondaryText,
//     fontWeight: 500,
//   }),
//   cardTitle: (c) => ({
//     margin: "6px 0 0",
//     fontSize: 15,
//     fontWeight: 500,
//     color: c.heading,
//     lineHeight: 1.35,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//   }),
//   priceRow: {
//     marginTop: 8,
//     display: "flex",
//     alignItems: "baseline",
//     gap: 10,
//     flexWrap: "wrap",
//   },
//   price: (c) => ({
//     fontSize: 15.5,
//     fontWeight: 700,
//     color: c.btnBg,
//     letterSpacing: "-0.01em",
//   }),
//   fromLabel: (c) => ({
//     fontSize: 11,
//     fontWeight: 600,
//     color: c.secondaryText,
//     letterSpacing: 0.3,
//     textTransform: "uppercase",
//   }),
//   oldPrice: (c) => ({
//     fontSize: 13.5,
//     color: c.secondaryText,
//     textDecoration: "line-through",
//   }),
//   mobileAddBtn: (c, enabled) => ({
//     width: "100%",
//     marginTop: 10,
//     padding: "9px 12px",
//     borderRadius: 10,
//     border: "none",
//     background: enabled ? c.btnBg : c.hoverBg,
//     color: enabled ? c.btnText : c.secondaryText,
//     fontSize: 12.5,
//     fontWeight: 700,
//     cursor: enabled ? "pointer" : "not-allowed",
//   }),

//   listCard: (c) => ({
//     display: "flex",
//     gap: 14,
//     padding: 12,
//     background: c.raisedBg,
//     border: `1px solid ${c.border}`,
//     borderRadius: 16,
//     cursor: "pointer",
//     alignItems: "stretch",
//     transition: "border-color 0.15s ease",
//   }),
//   listThumb: (c) => ({
//     width: 96,
//     height: 96,
//     borderRadius: 12,
//     background: c.hoverBg,
//     overflow: "hidden",
//     flexShrink: 0,
//   }),
//   listTopRow: {
//     display: "flex",
//     alignItems: "flex-start",
//     justifyContent: "space-between",
//     gap: 10,
//   },
//   listTitle: (c) => ({
//     margin: 0,
//     fontSize: 14.5,
//     fontWeight: 700,
//     color: c.heading,
//     whiteSpace: "nowrap",
//     overflow: "hidden",
//     textOverflow: "ellipsis",
//     minWidth: 0,
//     flex: 1,
//   }),
//   listDesc: (c) => ({
//     margin: "6px 0 0",
//     fontSize: 12.5,
//     color: c.secondaryText,
//     lineHeight: 1.5,
//     display: "-webkit-box",
//     WebkitLineClamp: 2,
//     WebkitBoxOrient: "vertical",
//     overflow: "hidden",
//   }),
//   listBottomRow: {
//     marginTop: "auto",
//     paddingTop: 8,
//     display: "flex",
//     alignItems: "center",
//     justifyContent: "space-between",
//     gap: 8,
//   },
//   listPrice: (c) => ({
//     fontSize: 17,
//     fontWeight: 800,
//     color: c.btnBg,
//     letterSpacing: "-0.01em",
//   }),
//   basketBtn: (c, enabled) => ({
//     width: 42,
//     height: 42,
//     borderRadius: "50%",
//     border: "none",
//     background: enabled ? c.btnBg : c.hoverBg,
//     color: enabled ? c.btnText : c.secondaryText,
//     cursor: enabled ? "pointer" : "not-allowed",
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     flexShrink: 0,
//   }),

//   emptyWrap: (c) => ({
//     textAlign: "center",
//     padding: "60px 24px",
//     borderRadius: 16,
//     border: `1px dashed ${c.border}`,
//     background: c.raisedBg,
//   }),
//   emptyIcon: (c) => ({
//     width: 64,
//     height: 64,
//     borderRadius: 18,
//     background: c.hoverBg,
//     color: c.secondaryText,
//     display: "inline-flex",
//     alignItems: "center",
//     justifyContent: "center",
//     marginBottom: 16,
//   }),
//   emptyTitle: (c) => ({
//     margin: "0 0 6px",
//     fontSize: 17,
//     fontWeight: 800,
//     color: c.heading,
//   }),
//   emptyText: (c) => ({
//     margin: "0 auto 22px",
//     fontSize: 13.5,
//     color: c.secondaryText,
//     maxWidth: 420,
//     lineHeight: 1.55,
//   }),
//   emptySecondary: (c) => ({
//     padding: "10px 18px",
//     borderRadius: 10,
//     border: `1px solid ${c.border}`,
//     background: "transparent",
//     color: c.text,
//     fontSize: 13.5,
//     fontWeight: 600,
//     cursor: "pointer",
//   }),
// };





























// screens/marketplace/MarketplaceScreen.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShoppingCart,
  Clock,
  Package,
  Star,
  LayoutGrid,
  List,
  ShoppingBag,
  Truck,
  AlertCircle,
  BadgeCheck,
  Layers,
  RefreshCw,
  Boxes,
} from "lucide-react";
import { useAuth } from "../../providers/AuthProvider";
import { useTheme } from "../../hooks/useTheme";
import { useToast } from "../../providers/ToastProvider";
import { useCart } from "../../contexts/CartContext";
import StorefrontHeader from "../../components/StorefrontHeader";
import StorefrontNav from "../../components/StorefrontNav";
import { isWholesale, moqLabel } from "../../utils/variants";

const money = (v) => `₵${Number(v || 0).toFixed(2)}`;

// ── Derived product predicates ─────────────────────────────
const isReadyToShip = (p) =>
  p.sale_type === "DROPSHIPPING" ||
  p.sale_type === "BOTH" ||
  p.sale_type === "AVAILABLE_IN_GHANA";

const isAvailableInGhana = (p) => p.sale_type === "AVAILABLE_IN_GHANA";

const getCategory = (p) =>
  p.category_name || p.category?.name || p.sale_type_display || "Product";

const getDiscount = (product, price) => {
  if (product.has_variants) return null;
  const original =
    product.base_price_customer || product.compare_at_price || null;
  const now = Number(price || 0);
  const was = Number(original || 0);
  if (!was || was <= now) return null;
  const pct = Math.round((1 - now / was) * 100);
  return pct > 0 ? { pct, was } : null;
};

const getDisplayPrice = (product, preorder) => {
  const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
  if (product.has_variants) {
    const variantPrices = (product.variants || [])
      .map((v) =>
        isPreorderProduct && v.effective_pre_order_price
          ? Number(v.effective_pre_order_price)
          : Number(v.effective_price || v.base_price_customer)
      )
      .filter((n) => !isNaN(n) && n > 0);
    if (variantPrices.length > 0) return Math.min(...variantPrices);
  }
  if (isPreorderProduct && preorder) {
    return (
      preorder.item.effective_price ||
      preorder.item.price_customer ||
      product.effective_pre_order_price ||
      product.pre_order_price_customer
    );
  }
  return product.effective_price || product.base_price_customer;
};

export default function MarketplaceScreen({ defaultReadyOnly = false }) {
  const { authFetch, isAuthenticated } = useAuth();
  const { colors } = useTheme();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const { increment: incrementCart } = useCart();

  const search = params.get("search") || "";
  const filter = params.get("filter") || ""; // "" | "ghana" | "wholesale"
  const ghanaOnly = filter === "ghana";
  const wholesaleOnly = filter === "wholesale";

  const [products, setProducts] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("grid");
  const [wishlisted, setWishlisted] = useState(() => new Set());
  const [readyOnly, setReadyOnly] = useState(defaultReadyOnly);

  // ── Fetch ──────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      try {
        const query = new URLSearchParams();
        if (search) query.set("search", search);

        const [pRes, cRes] = await Promise.all([
          authFetch(`/marketplace/products/?${query}`),
          authFetch("/marketplace/pre-orders/?is_active=true"),
        ]);
        const [pData, cData] = await Promise.all([pRes.json(), cRes.json()]);
        if (!pRes.ok)
          throw new Error(pData.message || "Could not load products");
        if (!active) return;
        setProducts(pData.data || []);
        setCampaigns(cRes.ok ? cData.data || [] : []);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [authFetch, search, showToast]);

  // ── Derived ────────────────────────────────────────────
  const campaignByProduct = useMemo(() => {
    const map = new Map();
    campaigns.forEach((c) => {
      const items = c.products || c.pre_order_products || [];
      items.forEach((item) =>
        map.set(String(item.product ?? item.product_id), {
          campaign: c,
          item,
        })
      );
    });
    return map;
  }, [campaigns]);

  const liveCampaigns = useMemo(
    () =>
      campaigns.filter((c) => {
        const now = Date.now();
        return (
          c.is_active &&
          new Date(c.start_date).getTime() <= now &&
          new Date(c.end_date).getTime() >= now
        );
      }),
    [campaigns]
  );

  /**
   * The visible products array after all filters are applied.
   * Two independent axes:
   *   1. Ghana filter (from nav) — hides non-Ghana products entirely.
   *   2. Ready filter (from toolbar toggle) — hides non-ready products.
   * Both are client-side and compose freely.
   */
  const visibleProducts = useMemo(() => {
    let list = products;
    if (ghanaOnly) list = list.filter(isAvailableInGhana);
    if (wholesaleOnly) list = list.filter(isWholesale);
    if (readyOnly) list = list.filter(isReadyToShip);
    return list;
  }, [products, ghanaOnly, wholesaleOnly, readyOnly]);

  const nextClosing = useMemo(() => {
    if (liveCampaigns.length === 0) return null;
    return [...liveCampaigns].sort(
      (a, b) => new Date(a.end_date) - new Date(b.end_date)
    )[0];
  }, [liveCampaigns]);

  // ── URL helpers ────────────────────────────────────────
  const onSearch = (q) => {
    const next = new URLSearchParams(params);
    if (q) next.set("search", q);
    else next.delete("search");
    setParams(next);
  };

  // ── Cart ───────────────────────────────────────────────
  const addToCart = async (product, e) => {
    e?.stopPropagation();
    if (!isAuthenticated) return navigate("/login?next=/marketplace");
    if (product.has_variants) {
      navigate(`/marketplace/products/${product.id}`);
      return;
    }

    const preorder = campaignByProduct.get(String(product.id));
    const itemType =
      product.sale_type === "PRE_ORDER" && preorder
        ? "PRE_ORDER"
        : "DROPSHIPPING";
    try {
      const res = await authFetch("/marketplace/cart/add/", {
        method: "POST",
        body: JSON.stringify({
          product: product.id,
          quantity: 1,
          item_type: itemType,
          ...(itemType === "PRE_ORDER" && {
            pre_order: preorder?.campaign.id,
          }),
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.message || Object.values(data.errors || {}).flat().join(" ")
        );
      }
      showToast("Added to cart", "success");
      incrementCart(1);
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const toggleWishlist = (product, e) => {
    e?.stopPropagation();
    if (!isAuthenticated) return navigate("/login?next=/marketplace");
    setWishlisted((prev) => {
      const next = new Set(prev);
      if (next.has(product.id)) next.delete(product.id);
      else next.add(product.id);
      return next;
    });
  };

  // ── Empty state title adapts to what's filtered out ────
  const emptyContext = search
    ? { title: "No matches", body: `Nothing matches “${search}”.` }
    : ghanaOnly
      ? {
        title: "Nothing available in Ghana yet",
        body:
          "We're adding products to this category. Meanwhile, browse the full marketplace.",
      }
      : wholesaleOnly
        ? {
          title: "No wholesale products yet",
          body:
            "Wholesale listings are coming. Browse the full marketplace in the meantime.",
        }
        : readyOnly
          ? {
            title: "Nothing ready to ship",
            body:
              "Turn off the ready-to-ship filter to see everything, or browse pre-order windows.",
          }
          : { title: "No products yet", body: "Check back soon." };

  return (
    <div style={styles.pageWrap}>
      {/* Slim search header — brand and nav live elsewhere */}
      <StorefrontHeader
        onSearch={onSearch}
        searchValue={search}
        searchPlaceholder="Search products…"
      />

      {/* Pill nav — the "where am I" strip */}
      <div style={{ marginBottom: 14 }}>
        <StorefrontNav />
      </div>

      {/* Toolbar — result count + ready-only toggle + view toggle */}
      <div style={styles.toolbar}>
        <button
          type="button"
          onClick={() => setReadyOnly((v) => !v)}
          style={styles.filterChip(colors, readyOnly)}
          aria-pressed={readyOnly}
        >
          <Truck size={13} />
          Ready to ship only
          <span style={styles.filterCount(colors, readyOnly)}>
            {products.filter(isReadyToShip).length}
          </span>
        </button>

        <div style={styles.toolbarRight}>
          <span style={styles.toolbarCount(colors)}>
            {loading
              ? "Loading…"
              : `${visibleProducts.length} product${visibleProducts.length === 1 ? "" : "s"
              }`}
            {search && !loading && (
              <>
                {" · "}
                <strong style={{ color: colors.text }}>“{search}”</strong>
              </>
            )}
          </span>

          <div style={styles.viewToggle(colors)}>
            <ViewToggleBtn
              active={viewMode === "grid"}
              onClick={() => setViewMode("grid")}
              colors={colors}
              label="Grid"
            >
              <LayoutGrid size={15} />
            </ViewToggleBtn>
            <ViewToggleBtn
              active={viewMode === "list"}
              onClick={() => setViewMode("list")}
              colors={colors}
              label="List"
            >
              <List size={15} />
            </ViewToggleBtn>
          </div>
        </div>
      </div>

      {/* Loading status strip */}
      {loading && (
        <div style={styles.loadingStrip(colors)} role="status" aria-live="polite">
          <span style={styles.loadingDot(colors)} />
          <span>Loading products from Azoemart…</span>
        </div>
      )}

      {/* Pre-order nudge — only when a window is live and not already filtered out */}
      {!loading && nextClosing && !readyOnly && !ghanaOnly && (
        <NudgeStrip
          campaign={nextClosing}
          count={liveCampaigns.length}
          colors={colors}
          onOpen={() => navigate("/preorder")}
        />
      )}

      {/* Content */}
      {loading ? (
        <SkeletonGrid count={8} colors={colors} />
      ) : visibleProducts.length === 0 ? (
        <EmptyState
          colors={colors}
          title={emptyContext.title}
          body={emptyContext.body}
          onClear={
            search
              ? () => onSearch("")
              : ghanaOnly || wholesaleOnly
                ? () => navigate("/marketplace")
                : readyOnly
                  ? () => setReadyOnly(false)
                  : null
          }
          clearLabel={
            search
              ? "Clear search"
              : ghanaOnly || wholesaleOnly
                ? "Browse everything"
                : readyOnly
                  ? "Show everything"
                  : null
          }
        />
      ) : viewMode === "grid" ? (
        <div className="mp-grid">
          {visibleProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              preorder={campaignByProduct.get(String(product.id))}
              colors={colors}
              wishlisted={wishlisted.has(product.id)}
              onOpen={() => navigate(`/marketplace/products/${product.id}`)}
              onAdd={(e) => addToCart(product, e)}
              onWishlist={(e) => toggleWishlist(product, e)}
            />
          ))}
        </div>
      ) : (
        <div className="mp-list">
          {visibleProducts.map((product) => (
            <ProductRow
              key={product.id}
              product={product}
              preorder={campaignByProduct.get(String(product.id))}
              colors={colors}
              onOpen={() => navigate(`/marketplace/products/${product.id}`)}
              onAdd={(e) => addToCart(product, e)}
            />
          ))}
        </div>
      )}

      <style>{`
        .mp-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 18px 12px;
        }
        @media (min-width: 560px) {
          .mp-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 22px 18px; }
        }
        @media (min-width: 900px) {
          .mp-grid { grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 26px 20px; }
        }
        @media (min-width: 1200px) {
          .mp-grid { grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 30px 22px; }
        }
        .mp-list { display: grid; grid-template-columns: minmax(0, 1fr); gap: 10px; }
        @media (min-width: 900px) {
          .mp-list { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
        }
        @media (hover: none) { .mp-quick-actions { display: none !important; } }
        .mp-card:hover .mp-quick-actions,
        .mp-card:focus-within .mp-quick-actions {
          opacity: 1 !important; transform: translateX(0) !important;
        }
        .mp-card:hover .mp-media img,
        .mp-card:focus-within .mp-media img { transform: scale(1.04); }
        @media (hover: hover) and (min-width: 640px) {
          .mp-add-mobile { display: none !important; }
        }

        @keyframes sf-skeleton {
          0%, 100% { opacity: 0.65; }
          50% { opacity: 0.30; }
        }
        @keyframes sf-pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.4); opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════
   Nudge strip
   ══════════════════════════════════════════════════════════ */

const NudgeStrip = ({ campaign, count, colors, onOpen }) => {
  const end = new Date(campaign.end_date).getTime();
  const daysLeft = Math.max(0, Math.ceil((end - Date.now()) / 86400000));
  const urgent = daysLeft <= 2;
  const fg = urgent ? colors.error : colors.warning;
  const bg = urgent ? colors.errorBg : colors.warningBg;

  return (
    <button type="button" onClick={onOpen} style={styles.nudge(fg, bg)}>
      <span style={styles.nudgeIcon(fg)}>
        <Clock size={15} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={styles.nudgeTitle(colors)}>
          {count === 1
            ? "A pre-order window is open"
            : `${count} pre-order windows are open`}
        </div>
        <div style={styles.nudgeSub(colors)}>
          {campaign.name} ·{" "}
          {daysLeft === 0
            ? "closing today"
            : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left`}
        </div>
      </div>
      <span style={{ fontSize: 12, fontWeight: 700, color: fg }}>View</span>
    </button>
  );
};

/* ══════════════════════════════════════════════════════════
   Cards
   ══════════════════════════════════════════════════════════ */

const ViewToggleBtn = ({ active, onClick, colors, children, label }) => (
  <button
    type="button"
    onClick={onClick}
    aria-label={label}
    style={{
      padding: "7px 9px",
      border: "none",
      background: active ? colors.btnBg : "transparent",
      color: active ? colors.btnText : colors.secondaryText,
      cursor: "pointer",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {children}
  </button>
);

const Rating = ({ value, count, colors, compact }) => {
  const v = Number(value || 0);
  if (!v) return null;
  return (
    <span style={styles.rating(colors)}>
      <Star size={compact ? 11 : 12} fill={colors.warning} color={colors.warning} />
      <span style={{ color: colors.text, fontWeight: 700 }}>{v.toFixed(1)}</span>
      {!compact && count > 0 && <span>({count})</span>}
    </span>
  );
};

const StatusPill = ({ status, colors, floating }) => {
  const Icon = status.icon;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: "5px 11px",
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: 0.2,
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        background: status.bg,
        color: status.fg,
        ...(floating
          ? {
            position: "absolute",
            top: 10,
            left: 10,
            backdropFilter: "blur(8px)",
          }
          : {}),
      }}
    >
      <Icon size={10} />
      {status.label}
    </span>
  );
};

const DiscountBadge = ({ pct, colors }) => (
  <span
    style={{
      position: "absolute",
      top: 10,
      left: 10,
      padding: "5px 10px",
      borderRadius: 999,
      background: colors.btnBg,
      color: colors.btnText,
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: 0.2,
    }}
  >
    -{pct}%
  </span>
);

const GhanaBadge = ({ colors }) => (
  <span
    style={{
      position: "absolute",
      top: 10,
      right: 10,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "5px 10px",
      borderRadius: 999,
      background: colors.raisedBg,
      color: colors.btnBg,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      boxShadow: `0 2px 8px ${colors.shadow}`,
    }}
  >
    <BadgeCheck size={10} />
    Ghana
  </span>
);

const ShippingBadge = ({ colors, fee }) => {
  if (!fee || fee <= 0) return null;
  return (
    <span
      title="Estimated shipping. Paid separately when the item arrives in Ghana."
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        fontSize: 10.5,
        color: colors.secondaryText,
        fontWeight: 600,
      }}
    >
      <Truck size={10} /> ~{money(fee)} ship
    </span>
  );
};
const VariantHint = ({ colors, count }) => (
  <span
    style={{
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      fontSize: 10.5,
      fontWeight: 700,
      padding: "2px 7px",
      borderRadius: 999,
      background: colors.hoverBg,
      color: colors.secondaryText,
      letterSpacing: 0.2,
    }}
  >
    <Layers size={10} />
    {count} variants
  </span>
);

const QuickActions = ({ colors, onAdd, canBuy, onWishlist, wishlisted }) => (
  <div
    className="mp-quick-actions"
    style={{
      position: "absolute",
      top: 10,
      right: 10,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      opacity: 0,
      transform: "translateX(6px)",
      transition: "opacity 0.2s ease, transform 0.2s ease",
    }}
  >
    <QuickBtn
      colors={colors}
      aria-label="Wishlist"
      onClick={(e) => {
        e.stopPropagation();
        onWishlist?.();
      }}
    >
      <ShoppingBag
        size={14}
        fill={wishlisted ? colors.error : "none"}
        color={wishlisted ? colors.error : colors.text}
      />
    </QuickBtn>
    <QuickBtn
      colors={colors}
      aria-label="Add to cart"
      onClick={(e) => {
        e.stopPropagation();
        if (canBuy) onAdd(e);
      }}
      disabled={!canBuy}
    >
      <ShoppingCart size={14} color={canBuy ? colors.text : colors.secondaryText} />
    </QuickBtn>
  </div>
);

const QuickBtn = ({ colors, children, onClick, disabled, ...rest }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    {...rest}
    style={{
      width: 34,
      height: 34,
      borderRadius: "50%",
      border: "none",
      background: colors.raisedBg,
      color: colors.text,
      cursor: disabled ? "not-allowed" : "pointer",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      boxShadow: `0 2px 8px ${colors.shadow}`,
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {children}
  </button>
);

const ProductCard = ({
  product,
  preorder,
  colors,
  onOpen,
  onAdd,
  onWishlist,
  wishlisted,
}) => {
  const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
  const hasVariants = !!product.has_variants;
  const isGhana = product.sale_type === "AVAILABLE_IN_GHANA";
  const shownPrice = getDisplayPrice(product, preorder);

  const inStock = product.is_in_stock;
  const canBuy = !hasVariants && (isPreorderProduct ? !!preorder : inStock);
  const discount = getDiscount(product, shownPrice);
  const shippingFee = Number(product.shipping_fee || 0);

  const status = hasVariants
    ? { label: "Options", fg: colors.linkColor, bg: colors.hoverBg, icon: Layers }
    : isPreorderProduct
      ? preorder
        ? { label: "Pre-order", fg: colors.warning, bg: colors.warningBg, icon: Clock }
        : { label: "No window", fg: colors.secondaryText, bg: colors.hoverBg, icon: Clock }
      : inStock
        ? { label: "In stock", fg: colors.success, bg: colors.successBg, icon: Truck }
        : { label: "Sold out", fg: colors.secondaryText, bg: colors.hoverBg, icon: AlertCircle };

  const img =
    product.thumbnail_image_url ||
    product.main_image_url ||
    product.main_image;

  return (
    <article
      onClick={onOpen}
      className="mp-card"
      style={{ cursor: "pointer", minWidth: 0 }}
    >
      <div className="mp-media" style={styles.media(colors)}>
        {img ? (
          <img src={img} alt={product.name} loading="lazy" style={styles.mediaImg} />
        ) : (
          <div style={styles.mediaPlaceholder(colors)}>
            <Package size={28} />
          </div>
        )}

        {discount && !isPreorderProduct && !hasVariants ? (
          <DiscountBadge pct={discount.pct} colors={colors} />
        ) : (
          <StatusPill status={status} colors={colors} floating />
        )}

        {isWholesale(product) ? (
          <WholesaleBadge colors={colors} moq={product.moq} />
        ) : isGhana ? (
          <GhanaBadge colors={colors} />
        ) : null}

        <QuickActions
          colors={colors}
          canBuy={canBuy}
          onAdd={onAdd}
          onWishlist={onWishlist}
          wishlisted={wishlisted}
        />
      </div>

      <div style={styles.metaRow}>
        <span style={styles.category(colors)}>{getCategory(product)}</span>
        {hasVariants ? (
          <VariantHint colors={colors} count={(product.variants || []).length} />
        ) : (
          <Rating
            value={product.average_rating}
            count={product.review_count}
            colors={colors}
            compact
          />
        )}
      </div>

      <h3 style={styles.cardTitle(colors)}>{product.name}</h3>

      <div style={styles.priceRow}>
        {hasVariants && <span style={styles.fromLabel(colors)}>From</span>}
        <span style={styles.price(colors)}>{money(shownPrice)}</span>
        {discount && (
          <span style={styles.oldPrice(colors)}>{money(discount.was)}</span>
        )}
        {shippingFee > 0 && <ShippingBadge colors={colors} fee={shippingFee} />}
      </div>

      <button
        type="button"
        disabled={!canBuy && !hasVariants}
        onClick={onAdd}
        className="mp-add-mobile"
        style={styles.mobileAddBtn(colors, canBuy || hasVariants)}
      >
        {hasVariants
          ? "Choose options"
          : isPreorderProduct
            ? preorder
              ? "Join pre-order"
              : "Not in a window"
            : inStock
              ? "Add to cart"
              : "Sold out"}
      </button>
    </article>
  );
};

const ProductRow = ({ product, preorder, colors, onOpen, onAdd }) => {
  const isPreorderProduct = product.sale_type === "PRE_ORDER" || !!preorder;
  const hasVariants = !!product.has_variants;
  const isGhana = product.sale_type === "AVAILABLE_IN_GHANA";
  const shownPrice = getDisplayPrice(product, preorder);

  const inStock = product.is_in_stock;
  const canBuy = !hasVariants && (isPreorderProduct ? !!preorder : inStock);
  const shippingFee = Number(product.shipping_fee || 0);

  const status = hasVariants
    ? { label: "Options", fg: colors.linkColor, bg: colors.hoverBg, icon: Layers }
    : isPreorderProduct
      ? preorder
        ? { label: "Pre-order", fg: colors.warning, bg: colors.warningBg, icon: Clock }
        : { label: "No window", fg: colors.secondaryText, bg: colors.hoverBg, icon: Clock }
      : inStock
        ? { label: "In stock", fg: colors.success, bg: colors.successBg, icon: Truck }
        : { label: "Sold out", fg: colors.secondaryText, bg: colors.hoverBg, icon: AlertCircle };

  const img =
    product.thumbnail_image_url ||
    product.main_image_url ||
    product.main_image;

  return (
    <article
      onClick={onOpen}
      style={styles.listCard(colors)}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = colors.btnBg;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = colors.border;
      }}
    >
      <div style={styles.listThumb(colors)}>
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            style={{ width: "100%", height: "100%", objectFit: "cover" }}
          />
        ) : (
          <div style={styles.mediaPlaceholder(colors)}>
            <Package size={22} />
          </div>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        <div style={styles.listTopRow}>
          <h3 style={styles.listTitle(colors)}>{product.name}</h3>
          <StatusPill status={status} colors={colors} />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            marginTop: 4,
            flexWrap: "wrap",
          }}
        >
          {isGhana && (
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 3,
                fontSize: 10.5,
                fontWeight: 700,
                padding: "2px 7px",
                borderRadius: 999,
                background: `${colors.btnBg}18`,
                color: colors.btnBg,
                letterSpacing: 0.3,
                textTransform: "uppercase",
              }}
            >
              <BadgeCheck size={10} /> Ghana
            </span>
          )}
          {hasVariants && (
            <VariantHint colors={colors} count={(product.variants || []).length} />
          )}
        </div>
        <p style={styles.listDesc(colors)}>
          {product.short_description || "—"}
        </p>
        <div style={styles.listBottomRow}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
            {hasVariants && <span style={styles.fromLabel(colors)}>From</span>}
            <span style={styles.listPrice(colors)}>{money(shownPrice)}</span>
            {shippingFee > 0 && <ShippingBadge colors={colors} fee={shippingFee} />}
          </div>
          <button
            type="button"
            disabled={!canBuy && !hasVariants}
            onClick={onAdd}
            aria-label={
              hasVariants
                ? `Choose options for ${product.name}`
                : `Add ${product.name} to cart`
            }
            style={styles.basketBtn(colors, canBuy || hasVariants)}
          >
            <ShoppingCart size={18} />
          </button>
        </div>
      </div>
    </article>
  );
};

/* ── Skeleton / Empty ────────────────────────────────────── */

const SkeletonGrid = ({ count, colors }) => (
  <div className="mp-grid">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} style={{ minWidth: 0 }}>
        <div
          style={{
            aspectRatio: "1 / 1",
            borderRadius: 18,
            background: colors.hoverBg,
            animation: "sf-skeleton 1.6s ease-in-out infinite",
          }}
        />
        <div style={{ marginTop: 12, display: "grid", gap: 6 }}>
          <div
            style={{
              height: 10,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "60%",
              animation: "sf-skeleton 1.6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "80%",
              animation: "sf-skeleton 1.6s ease-in-out infinite",
            }}
          />
          <div
            style={{
              height: 12,
              borderRadius: 4,
              background: colors.hoverBg,
              width: "40%",
              animation: "sf-skeleton 1.6s ease-in-out infinite",
            }}
          />
        </div>
      </div>
    ))}
  </div>
);

const EmptyState = ({ colors, title, body, onClear, clearLabel }) => (
  <div style={styles.emptyWrap(colors)}>
    <div style={styles.emptyIcon(colors)}>
      <Package size={26} />
    </div>
    <h3 style={styles.emptyTitle(colors)}>{title}</h3>
    <p style={styles.emptyText(colors)}>{body}</p>
    {onClear && clearLabel && (
      <button type="button" onClick={onClear} style={styles.emptySecondary(colors)}>
        <RefreshCw size={14} />
        {clearLabel}
      </button>
    )}
  </div>
);





const WholesaleBadge = ({ colors, moq }) => (
  <span
    style={{
      position: "absolute",
      top: 10,
      right: 10,
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      padding: "5px 10px",
      borderRadius: 999,
      background: colors.raisedBg,
      color: colors.btnBg,
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.3,
      textTransform: "uppercase",
      boxShadow: `0 2px 8px ${colors.shadow}`,
    }}
  >
    <Boxes size={10} />
    MOQ {moq}
  </span>
);

/* ══════════════════════════════════════════════════════════
   Styles
   ══════════════════════════════════════════════════════════ */

const styles = {
  pageWrap: {
    maxWidth: 1180,
    margin: "0 auto",
    padding: "20px 16px 60px",
  },

  toolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    marginBottom: 16,
    flexWrap: "wrap",
  },
  filterChip: (c, active) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "8px 12px",
    borderRadius: 999,
    border: `1px solid ${active ? c.btnBg : c.border}`,
    background: active ? c.btnBg : c.raisedBg,
    color: active ? c.btnText : c.text,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.15s ease",
  }),
  filterCount: (c, active) => ({
    fontSize: 11,
    fontWeight: 800,
    padding: "1px 7px",
    borderRadius: 999,
    background: active ? "rgba(255,255,255,0.22)" : c.hoverBg,
    color: active ? "#fff" : c.secondaryText,
  }),
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    marginLeft: "auto",
    flexWrap: "wrap",
  },
  toolbarCount: (c) => ({
    fontSize: 12.5,
    color: c.secondaryText,
  }),
  viewToggle: (c) => ({
    display: "inline-flex",
    border: `1px solid ${c.border}`,
    borderRadius: 10,
    overflow: "hidden",
    background: c.raisedBg,
  }),

  loadingStrip: (c) => ({
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "10px 14px",
    borderRadius: 10,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    color: c.secondaryText,
    fontSize: 12.5,
    fontWeight: 600,
    marginBottom: 16,
  }),
  loadingDot: (c) => ({
    width: 8,
    height: 8,
    borderRadius: "50%",
    background: c.btnBg,
    animation: "sf-pulse 1.2s ease-in-out infinite",
    flexShrink: 0,
  }),

  nudge: (fg, bg) => ({
    display: "flex",
    alignItems: "center",
    gap: 12,
    width: "100%",
    padding: "10px 14px",
    marginBottom: 16,
    borderRadius: 12,
    border: `1px solid ${fg}55`,
    background: bg,
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
  }),
  nudgeIcon: (fg) => ({
    width: 32,
    height: 32,
    borderRadius: 9,
    background: fg,
    color: "#fff",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),
  nudgeTitle: (c) => ({
    fontSize: 13,
    fontWeight: 700,
    color: c.heading,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  nudgeSub: (c) => ({
    fontSize: 11.5,
    color: c.secondaryText,
    marginTop: 2,
  }),

  media: (c) => ({
    position: "relative",
    aspectRatio: "1 / 1",
    borderRadius: 18,
    overflow: "hidden",
    background: c.hoverBg,
  }),
  mediaImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    display: "block",
    transition: "transform 0.5s ease",
  },
  mediaPlaceholder: (c) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: c.secondaryText,
  }),
  metaRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    marginTop: 12,
  },
  category: (c) => ({
    fontSize: 12,
    color: c.secondaryText,
    fontWeight: 500,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  }),
  rating: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 11.5,
    color: c.secondaryText,
    fontWeight: 500,
  }),
  cardTitle: (c) => ({
    margin: "6px 0 0",
    fontSize: 15,
    fontWeight: 500,
    color: c.heading,
    lineHeight: 1.35,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  priceRow: {
    marginTop: 8,
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    flexWrap: "wrap",
  },
  price: (c) => ({
    fontSize: 15.5,
    fontWeight: 700,
    color: c.btnBg,
    letterSpacing: "-0.01em",
  }),
  fromLabel: (c) => ({
    fontSize: 11,
    fontWeight: 600,
    color: c.secondaryText,
    letterSpacing: 0.3,
    textTransform: "uppercase",
  }),
  oldPrice: (c) => ({
    fontSize: 13.5,
    color: c.secondaryText,
    textDecoration: "line-through",
  }),
  mobileAddBtn: (c, enabled) => ({
    width: "100%",
    marginTop: 10,
    padding: "9px 12px",
    borderRadius: 10,
    border: "none",
    background: enabled ? c.btnBg : c.hoverBg,
    color: enabled ? c.btnText : c.secondaryText,
    fontSize: 12.5,
    fontWeight: 700,
    cursor: enabled ? "pointer" : "not-allowed",
  }),

  listCard: (c) => ({
    display: "flex",
    gap: 14,
    padding: 12,
    background: c.raisedBg,
    border: `1px solid ${c.border}`,
    borderRadius: 16,
    cursor: "pointer",
    alignItems: "stretch",
    transition: "border-color 0.15s ease",
  }),
  listThumb: (c) => ({
    width: 96,
    height: 96,
    borderRadius: 12,
    background: c.hoverBg,
    overflow: "hidden",
    flexShrink: 0,
  }),
  listTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: 10,
  },
  listTitle: (c) => ({
    margin: 0,
    fontSize: 14.5,
    fontWeight: 700,
    color: c.heading,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
    flex: 1,
  }),
  listDesc: (c) => ({
    margin: "6px 0 0",
    fontSize: 12.5,
    color: c.secondaryText,
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  }),
  listBottomRow: {
    marginTop: "auto",
    paddingTop: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
  },
  listPrice: (c) => ({
    fontSize: 17,
    fontWeight: 800,
    color: c.btnBg,
    letterSpacing: "-0.01em",
  }),
  basketBtn: (c, enabled) => ({
    width: 42,
    height: 42,
    borderRadius: "50%",
    border: "none",
    background: enabled ? c.btnBg : c.hoverBg,
    color: enabled ? c.btnText : c.secondaryText,
    cursor: enabled ? "pointer" : "not-allowed",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  }),

  emptyWrap: (c) => ({
    textAlign: "center",
    padding: "60px 24px",
    borderRadius: 16,
    border: `1px dashed ${c.border}`,
    background: c.raisedBg,
  }),
  emptyIcon: (c) => ({
    width: 64,
    height: 64,
    borderRadius: 18,
    background: c.hoverBg,
    color: c.secondaryText,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
  }),
  emptyTitle: (c) => ({
    margin: "0 0 6px",
    fontSize: 17,
    fontWeight: 800,
    color: c.heading,
  }),
  emptyText: (c) => ({
    margin: "0 auto 22px",
    fontSize: 13.5,
    color: c.secondaryText,
    maxWidth: 420,
    lineHeight: 1.55,
  }),
  emptySecondary: (c) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "10px 18px",
    borderRadius: 10,
    border: `1px solid ${c.border}`,
    background: "transparent",
    color: c.text,
    fontSize: 13.5,
    fontWeight: 600,
    cursor: "pointer",
  }),
};